import { Chip, ChipProps } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  HelpOutline as HelpIcon,
  RemoveCircleOutline as VaziaIcon,
} from '@mui/icons-material';
import { formatarDataHoraSegundos } from './formatToView';

export type MonitorPingStatus = 'online' | 'offline' | 'maintenance' | 'unknown';
export type MonitorCtoStatus = 'online' | 'offline' | 'vazia';

export function formatMonitorDate(value?: string | Date | null): string {
  if (!value) return '—';
  const str = typeof value === 'string' ? value : value.toISOString();
  const dateOnly = str.substring(0, 10);
  const parts = dateOnly.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateOnly;
}

export function formatMonitorDateTime(value?: string | Date | null): string {
  if (!value) return '—';
  const str = typeof value === 'string' ? value : value.toISOString();
  return formatarDataHoraSegundos(str);
}

export function getMonitorStatusLabel(status?: string | null): string {
  switch (status) {
    case 'online':
      return 'Online';
    case 'offline':
      return 'Offline';
    case 'vazia':
      return 'Vazia';
    case 'maintenance':
      return 'Manutenção';
    case 'unknown':
      return 'Desconhecido';
    default:
      return status || '—';
  }
}

export function getMonitorStatusColor(
  status?: string | null
): ChipProps['color'] {
  switch (status) {
    case 'online':
      return 'success';
    case 'offline':
      return 'error';
    case 'vazia':
      return 'default';
    case 'maintenance':
      return 'warning';
    case 'unknown':
      return 'default';
    default:
      return 'default';
  }
}

export function getMonitorStatusIcon(status?: string | null) {
  switch (status) {
    case 'online':
      return <CheckCircleIcon fontSize="small" />;
    case 'offline':
      return <ErrorIcon fontSize="small" />;
    case 'vazia':
      return <VaziaIcon fontSize="small" />;
    case 'maintenance':
      return <PauseIcon fontSize="small" />;
    default:
      return <HelpIcon fontSize="small" />;
  }
}

interface MonitorStatusChipProps {
  status?: string | null;
  size?: ChipProps['size'];
  variant?: ChipProps['variant'];
}

export function MonitorStatusChip({
  status,
  size = 'small',
  variant = 'outlined',
}: MonitorStatusChipProps) {
  return (
    <Chip
      icon={getMonitorStatusIcon(status)}
      label={getMonitorStatusLabel(status)}
      color={getMonitorStatusColor(status)}
      size={size}
      variant={variant}
      sx={{ fontSize: size === 'small' ? '0.7rem' : undefined, height: size === 'small' ? 22 : undefined }}
    />
  );
}

export function normalizeStatusLabel(status?: string | null): string {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getServiceStatusColor(status?: string | null): ChipProps['color'] {
  switch (status) {
    case 'ativo':
      return 'success';
    case 'bloqueado':
    case 'suspenso':
      return 'warning';
    case 'cancelado':
      return 'error';
    case 'aguardando_instalacao':
    case 'aguardando_assinatura':
      return 'info';
    default:
      return 'default';
  }
}

export function getConnectionStatusColor(status?: string | null): ChipProps['color'] {
  if (status === 'Online') return 'success';
  if (status === 'Offline') return 'error';
  return 'default';
}

export interface MonitorTimestamps {
  ultimoOnlineEm?: string | Date | null;
  ultimoOfflineEm?: string | Date | null;
  ultimaVerificacaoEm?: string | Date | null;
  ultimoPingEm?: string | Date | null;
  latenciaMs?: number | null;
}
